import { type FC, Fragment, useEffect, useMemo, useState } from 'react';
import type { GameV1 } from '@/api/games';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { getGamesPlugs } from '@/helpers/game';
import { GameCardFactory, ShowAllGameCard } from '@/modules/game/components';
import { useGetCommonStore } from '@/store';
import { defaultGridOptions, type GridDimensions } from '../helpers';
import { useGridDimensions } from '../hooks';

import { ScGamesGrid, ScGamesGridRow } from '../styled';

export interface GamesGridStaticPros extends GridDimensions {
  games: GameV1[];
  loading?: boolean;
  plugGray?: boolean;
  noShowMore?: boolean;
  totalCount?: number;
  isSearchModal?: boolean;
  onShowMoreButtonClick?: () => void;
  onPlayClick?: (game: GameV1) => void;
  onDemoClick?: (game: GameV1) => void;
}

export const GamesGridStatic: FC<GamesGridStaticPros> = ({
  perChunkObj,
  gridOptions = defaultGridOptions,
  games,
  loading,
  plugGray = true,
  noShowMore,
  totalCount,
  isSearchModal,
  onShowMoreButtonClick,
  onPlayClick,
  onDemoClick,
}) => {
  const [currentChunk, setCurrentChunk] = useState(1);
  const [gamePlugs, setGamePlugs] = useState<GameV1[]>([]);

  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const { perChunk, gridColumns } = useGridDimensions({
    perChunkObj,
    gridOptions,
  });

  useEffect(() => {
    if (totalCount) return;
    setCurrentChunk(1);
  }, [totalCount, games]);

  const gridArea = useMemo(
    () => currentChunk * perChunk,
    [currentChunk, perChunk]
  );

  const isShowMore = useMemo(() => {
    if (noShowMore) return false;
    if (totalCount) return totalCount > perChunk;
    return games.length > perChunk;
  }, [games.length, noShowMore, perChunk, totalCount]);

  const gamesChunk = useMemo(
    () => games.slice(0, isShowMore ? gridArea - 1 : gridArea),
    [games, isShowMore, gridArea]
  );

  const nextChunk = () => {
    setCurrentChunk((s) => s + 1);
  };

  const isShowMoreMode = useMemo(() => {
    const gamesChunkCount = gamesChunk.length;
    const gamesCount = games.length;

    if (totalCount) {
      return totalCount - gamesChunkCount !== 0;
    }

    return gamesCount - gamesChunkCount !== 0;
  }, [games, gamesChunk, totalCount]);

  useEffect(() => {
    if (
      ((totalCount && perChunk > totalCount) || perChunk > games.length) &&
      gamesChunk.length < gridArea
    ) {
      const plugs = getGamesPlugs(gamesChunk, gridColumns, 1);
      setGamePlugs(plugs);
    } else {
      setGamePlugs([]);
    }
  }, [games, totalCount, perChunk, gamesChunk, gridArea, gridColumns]);

  const handleShowMoreButtonClick = () => {
    if (!isShowMoreMode) {
      setCurrentChunk(1);
      return;
    }

    if (onShowMoreButtonClick) {
      onShowMoreButtonClick();
      nextChunk();
    } else {
      nextChunk();
    }
  };

  const mode =
    (!isSearchModal && plugGray) || isMobileDevice ? 'dark' : 'light';

  return (
    <ScGamesGrid>
      {loading && <GamesSkeleton isSearchModal={isSearchModal} />}
      {!!gamesChunk.length && (
        <ScGamesGridRow {...gridOptions}>
          {[...gamesChunk, ...gamePlugs].map((game, index) => (
            <Fragment key={game?.id ?? `plug-${index}`}>
              {game?.id ? (
                <GameCardFactory
                  game={game}
                  mode={mode}
                  onDemoClick={() => onDemoClick && onDemoClick(game)}
                  onPlayClick={() => onPlayClick && onPlayClick(game)}
                />
              ) : (
                <GameCardFactory isEmpty mode={mode} />
              )}
            </Fragment>
          ))}
          {isShowMore && (
            <ShowAllGameCard
              isShowMoreMode={isShowMoreMode}
              mode={mode}
              onClick={handleShowMoreButtonClick}
            />
          )}
        </ScGamesGridRow>
      )}
    </ScGamesGrid>
  );
};
